import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import {
  POTENTIAL_CLIENT_REJECTED_STATUS,
  POTENTIAL_CLIENT_REJECTION_REASONS,
  POTENTIAL_CLIENT_STANDBY_STATUS,
  PotentialClient,
  PotentialClientStatus,
} from 'src/app/models/potential-client.interface';
import { ApiService } from 'src/app/services/api.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';

@Component({
  selector: 'app-potential-client-change-status-dialog',
  templateUrl: './potential-client-change-status-dialog.component.html',
  styleUrls: ['./potential-client-change-status-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelect,
    MatOption,
    MatInput,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDialogActions,
    MatButton,
    MatIcon,
  ],
})
export class PotentialClientChangeStatusDialogComponent implements OnInit {
  protected fb = inject(FormBuilder);
  dialogRef = inject<MatDialogRef<PotentialClientChangeStatusDialogComponent>>(MatDialogRef);
  data = inject(MAT_DIALOG_DATA);
  private api = inject(ApiService);

  form: FormGroup;
  potential_client_statuses: PotentialClientStatus[] = [];
  current_potential_client: PotentialClient;
  rejectionReasons = POTENTIAL_CLIENT_REJECTION_REASONS;
  rejectedSlug = POTENTIAL_CLIENT_REJECTED_STATUS;
  standbySlug = POTENTIAL_CLIENT_STANDBY_STATUS;
  minNotifyDate = new Date();

  constructor() {
    const data = this.data;

    this.current_potential_client = data.potential_client;

    this.form = this.fb.group({
      potential_client_status_slug: ['', Validators.required],
      rejection_reason: [null],
      rejection_justification: [''],
      standby_notify_at: [null],
    });
  }

  ngOnInit(): void {
    this.fetchStatuses();
    this.form.get('potential_client_status_slug')?.valueChanges.subscribe(slug => {
      const reasonControl = this.form.get('rejection_reason');
      const standbyDateControl = this.form.get('standby_notify_at');

      if (slug === this.rejectedSlug) {
        reasonControl?.setValidators([Validators.required]);
      } else {
        reasonControl?.clearValidators();
        reasonControl?.setValue(null);
        this.form.get('rejection_justification')?.setValue('');
      }

      if (slug === this.standbySlug) {
        standbyDateControl?.setValidators([Validators.required]);
      } else {
        standbyDateControl?.clearValidators();
        standbyDateControl?.setValue(null);
      }

      reasonControl?.updateValueAndValidity();
      standbyDateControl?.updateValueAndValidity();
    });
  }

  get requiresRejection(): boolean {
    return this.form.value.potential_client_status_slug === this.rejectedSlug;
  }

  get requiresStandbyDate(): boolean {
    return this.form.value.potential_client_status_slug === this.standbySlug;
  }

  close(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (!this.form.valid) return;
    const value = { ...this.form.value };
    if (value.potential_client_status_slug !== this.rejectedSlug) {
      delete value.rejection_reason;
      delete value.rejection_justification;
    }
    if (value.potential_client_status_slug !== this.standbySlug) {
      delete value.standby_notify_at;
    } else if (value.standby_notify_at instanceof Date) {
      const year = value.standby_notify_at.getFullYear();
      const month = String(value.standby_notify_at.getMonth() + 1).padStart(2, '0');
      const day = String(value.standby_notify_at.getDate()).padStart(2, '0');
      value.standby_notify_at = `${year}-${month}-${day}`;
    }
    this.dialogRef.close(value);
  }

  private fetchStatuses() {
    this.api.getPotentialClientStatus().subscribe(data => {
      this.potential_client_statuses = data;
    });
  }
}
